import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Beaker, BookOpen, Bookmark, Camera, Flame, Gift, LayoutDashboard, ListChecks, LogOut, Menu, MessageCircle, ShieldCheck, Sparkles, UserCircle2, Users, Wallet, Newspaper } from "lucide-react";
import { cn } from "@/lib/utils";
import { LegalFooter } from "@/components/LegalFooter";
import { TermsGate } from "@/components/TermsGate";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { GlowieIntroModal } from "@/components/GlowieIntroModal";
import Seo from "@/components/Seo";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import InviteButton from "@/components/InviteButton";
import { useLocation } from "react-router-dom";
import stickerStar from "@/assets/sticker-star.webp";

const navItems = [
  { to: "/app", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/app/feed", label: "Feed", icon: Newspaper },
  { to: "/app/friends", label: "Friends", icon: Users },
  { to: "/app/glow", label: "Daily Glow Score", icon: Sparkles },
  { to: "/app/routine", label: "My Routine", icon: ListChecks },
  { to: "/app/formulas", label: "Formulas", icon: Beaker },
  { to: "/app/saved", label: "Saved for later", icon: Bookmark },
  { to: "/app/chat", label: "Chat with Glowie", icon: MessageCircle },
  { to: "/app/conversations", label: "Your chats", icon: MessageCircle },
  { to: "/app/skin-analysis", label: "Skin Analysis", icon: Camera },
  { to: "/app/glow-up", label: "Glow-Up Reveal", icon: ArrowLeftRight },
  { to: "/app/roast", label: "Roast my shelf", icon: Flame },
  { to: "/app/ingredients", label: "Ingredients", icon: BookOpen },
  { to: "/app/referrals", label: "Invite friends", icon: Gift },
  { to: "/app/account", label: "Account", icon: UserCircle2 },
];

export const AppLayout = () => {
  const { user, signOut } = useAuth();
  const { balance } = useCredits();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Map routes to the feature flag they depend on, so users see a banner
  // instead of a silent failure when admins disable a feature.
  const featureForPath = (path: string): string | null => {
    if (path.startsWith("/app/chat") || path.startsWith("/app/conversations")) return "chat";
    if (path.startsWith("/app/roast")) return "roast_shelf";
    if (path.startsWith("/app/skin-analysis")) return "skin_analysis";
    if (path.startsWith("/app/glow-up")) return "glow_up";
    if (path.startsWith("/app/glow")) return "glow_checkin";
    if (path.startsWith("/app/routine")) return "suggest_routine";
    if (path.startsWith("/app/ingredients")) return "ingredient_lookup";
    if (path.startsWith("/app/formulas")) return "generate_formula";
    return null;
  };
  const activeFeature = featureForPath(location.pathname);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // Auto-close drawer on route change
  const closeMobile = () => setMobileOpen(false);

  const SidebarContent = ({ onNavigate }: { onNavigate?: () => void }) => (
    <>
      <Link
        to="/app"
        onClick={onNavigate}
        className="px-6 py-5 border-b border-border flex items-center gap-2 group"
      >
        <img src={stickerStar} alt="" className="h-7 w-7 group-hover:rotate-12 transition-transform" />
        <span className="font-display text-2xl font-semibold tracking-tight">Glowie</span>
      </Link>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary"
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink
            to="/app/admin/credits"
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-colors mt-2 border border-dashed border-border",
                isActive
                  ? "bg-primary text-primary-foreground border-primary"
                  : "text-foreground hover:bg-secondary"
              )
            }
          >
            <ShieldCheck className="h-4 w-4" />
            Admin: grant credits
          </NavLink>
        )}
        {isAdmin && (
          <NavLink
            to="/app/admin/analytics"
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-colors border border-dashed border-border",
                isActive
                  ? "bg-primary text-primary-foreground border-primary"
                  : "text-foreground hover:bg-secondary"
              )
            }
          >
            <ShieldCheck className="h-4 w-4" />
            Admin: analytics
          </NavLink>
        )}
        {isAdmin && (
          <NavLink
            to="/app/admin/system-health"
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-colors border border-dashed border-border",
                isActive
                  ? "bg-primary text-primary-foreground border-primary"
                  : "text-foreground hover:bg-secondary"
              )
            }
          >
            <ShieldCheck className="h-4 w-4" />
            Admin: system health
          </NavLink>
        )}
      </nav>
      <div className="p-4 border-t border-border space-y-3">
        <div className="text-xs text-muted-foreground">Signed in as ✨</div>
        <div className="text-sm truncate" title={user?.email ?? ""}>{user?.email}</div>
        <Button variant="outline" size="sm" onClick={handleSignOut} className="w-full">
          <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
        </Button>
      </div>
    </>
  );

  return (
    <TermsGate>
    <Seo
      title="Glowie"
      description="Your AI skincare workspace."
      noindex
    />
    <PaymentTestModeBanner />
    <div className="min-h-screen bg-gradient-cream flex">
      <aside className="w-64 border-r border-border bg-card/80 backdrop-blur hidden md:flex flex-col">
        <SidebarContent />
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card/80 backdrop-blur flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="h-9 w-9">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[85vw] max-w-xs flex flex-col">
                <SidebarContent onNavigate={closeMobile} />
              </SheetContent>
            </Sheet>
            <Link to="/app" className="flex items-center gap-1.5">
              <img src={stickerStar} alt="" className="h-5 w-5" />
              <span className="font-display text-lg font-semibold">Glowie</span>
            </Link>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <InviteButton variant="icon" />
            <Link
              to="/app/billing"
              className="flex items-center gap-1.5 px-2.5 md:px-3 py-1.5 rounded-full bg-gradient-hero text-primary-foreground text-xs md:text-sm hover:opacity-90 shadow-soft transition-opacity whitespace-nowrap"
            >
              <Wallet className="h-3.5 w-3.5" />
              <span className="font-semibold">{(balance ?? 0).toFixed(2)}</span>
              <span className="hidden sm:inline">credits</span>
            </Link>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          {activeFeature && <MaintenanceBanner feature={activeFeature} />}
          <Outlet />
        </main>
        <LegalFooter />
      </div>
      <GlowieIntroModal />
    </div>
    </TermsGate>
  );
};